/**
 * Image URL optimiser.
 *
 * Many menu item images point at full-size Unsplash originals (multi-MB each)
 * even though we display them at ~200 px thumbnails.  This helper appends
 * Unsplash's transform query so we only download what we need:
 *
 *    https://images.unsplash.com/photo-abc?w=320&q=70&fit=crop&auto=format
 *
 * Other hosts pass through untouched.
 *
 * Typical savings: a 3 MB photo → ~18 KB WebP at the same on-screen size.
 */

// `width` is the *rendered* CSS pixel width.  We oversample 2× by default for
// retina displays — bump it down via the second arg on truly low-end terminals.
export function optimizeImageUrl(url, width = 320, opts = {}) {
  if (!url || typeof url !== "string") return url;
  const { quality = 70, devicePixelRatio = 2 } = opts;

  // Only transform Unsplash URLs — other CDNs / data: URIs / blob: URLs go
  // through untouched.
  if (!url.includes("images.unsplash.com")) return url;

  // Strip any existing query so we don't fight the user's pre-baked params.
  const base = url.split("?")[0];
  const w = Math.round(width * devicePixelRatio);
  return `${base}?w=${w}&q=${quality}&fit=crop&auto=format`;
}

/** Tiny placeholder (≈1 KB) — instant render while the full image streams in. */
export function placeholderUrl(url) {
  if (!url || typeof url !== "string") return null;
  if (!url.includes("images.unsplash.com")) return null;
  const base = url.split("?")[0];
  return `${base}?w=20&q=30&blur=15&auto=format`;
}
